/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMutation } from "@apollo/client";
import { Box, Button, TextField, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import ConfirmationDialog from "../common/ConfirmationDialog";
import Error from "../common/Error";
import Loading from "../common/Loading";
import CustomSnackbar from "../common/Snackbar";
import Finish from "../images/finish.svg";
import { pages } from "../pages/pages";
import ExerciseOverview from "../session/ExerciseOverview";
import { LocationSessionState } from "../types";
import { getSettings } from "../utils/localStorage";
import { secondsToTime } from "../utils/timeHelpers";
import { addSettingMutation } from "./Summary.gql";
import { ButtonContainer, Container, ImageContainer, StyledImage } from "./Summary.sc";

interface SnackbarProps {
    open: boolean;
    text: string;
    variant: "success" | "error" | "warning";
}

const Summary: React.FunctionComponent = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [snackbar, setSnackbar] = React.useState<SnackbarProps>({ open: false, text: "", variant: "success" });
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [showSaveSetting, setShowSaveSetting] = React.useState(true);
    const [settingName, setSettingName] = React.useState<string>("");
    const [create, { loading }] = useMutation(addSettingMutation);

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbar({ open: false, text: "", variant: "success" });
    };

    const { session, error } = (location.state as LocationSessionState) ?? { session: undefined, error: true };

    React.useEffect(() => {
        if (session) {
            setSnackbar({ open: true, text: t("success_saving_session"), variant: "success" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveSettings = () => {
        const settings = getSettings();

        create({
            variables: {
                data: {
                    userId: localStorage.getItem("userId")!,
                    name: settingName,
                    discreet: settings.discreet === "yes" ? true : false,
                    favourite: settings.favourite === "yes" ? true : false,
                    categories: settings.categories ?? [],
                },
            },
        })
            .then(() => {
                setDialogOpen(false);
                setSnackbar({ open: true, text: t("success_saving_setting"), variant: "success" });
                setShowSaveSetting(false);
            })
            .catch(() => {
                setSnackbar({ open: true, text: t("error_saving_setting"), variant: "error" });
            });
    };

    return (
        <>
            <Container>
                {error && <Error text={t("error")} />}
                {loading && <Loading />}
                {!error && session && (
                    <>
                        <Typography variant="h4" color="primary" textAlign="center">
                            {t("well_done")}
                        </Typography>
                        <ImageContainer>
                            <StyledImage src={Finish} />
                        </ImageContainer>
                        <Typography textAlign="center">{t("summary", { time: secondsToTime(session?.durationTotal ?? 0) })}</Typography>
                        <ExerciseOverview session={session} />
                        <ButtonContainer>
                            {showSaveSetting && (
                                <Button
                                    onClick={() => {
                                        setDialogOpen(true);
                                    }}
                                >
                                    {t("save_used_settings")}
                                </Button>
                            )}
                            <Button color="primary" onClick={() => navigate(pages.profile)}>
                                {t("show_my_profile")}
                            </Button>
                            <div style={{ marginTop: 20 }}>
                                <Button variant="contained" color="primary" onClick={() => navigate(pages.home)}>
                                    {t("back_to_home")}
                                </Button>
                            </div>
                        </ButtonContainer>
                    </>
                )}
            </Container>
            <CustomSnackbar open={snackbar.open} onClose={handleClose} text={snackbar.text} variant={snackbar.variant} />
            <ConfirmationDialog
                open={dialogOpen}
                onClose={() => {
                    setDialogOpen(false);
                }}
                title="save_used_settings"
                saveButton={
                    <Button variant="contained" onClick={handleSaveSettings} disabled={settingName === undefined || settingName.length === 0}>
                        {t("save")}
                    </Button>
                }
            >
                <Box component="form" style={{ paddingTop: 20 }}>
                    <TextField
                        required
                        label={t("name_of_setting")}
                        placeholder={t("bus_station")}
                        fullWidth
                        value={settingName}
                        onChange={(e) => setSettingName(e.target.value)}
                    />
                </Box>
            </ConfirmationDialog>
        </>
    );
};

export default Summary;
